import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getUser } from "services/user";
import Card from "components/card";
import { useToast } from "components/Toast";

const UserProfileShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      try {
        const res = await getUser(id);
        setUser(res.data);
      } catch (error) {
        console.error("Failed to fetch user", error);
        toast.showError("Failed to load user profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, toast]);

  return (
    <Card extra="p-4">
      {loading ? (
        <p>Loading...</p>
      ) : user ? (
        <div className="md:flex md:items-start md:space-x-6">
          <div className="mb-4 md:mb-0">
            {user.pfp ? (
              <img
                src={
                  user.pfp.startsWith("http")
                    ? user.pfp
                    : user.pfp.startsWith("/")
                    ? `${(
                        process.env.REACT_APP_API_URL ||
                        "http://greaternunya.site/api"
                      ).replace(/\/api\/?$/, "")}${user.pfp}`
                    : `${(
                        process.env.REACT_APP_API_URL ||
                        "http://greaternunya.site/api"
                      ).replace(/\/api\/?$/, "")}/storage/${user.pfp}`
                }
                alt={`${user.firstname} ${user.lastname}`}
                className="h-32 w-32 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gray-200">
                <span className="text-xl text-gray-600">
                  {user.firstname?.charAt(0)}
                  {user.lastname?.charAt(0)}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">
              {user.firstname} {user.lastname}
            </h1>
            <p className="mb-2 text-sm text-gray-500">{user.email}</p>
            <div className="grid grid-cols-1 gap-2 text-sm text-gray-600 sm:grid-cols-2">
              {user.phone && (
                <div>
                  <strong className="block text-gray-800">Phone</strong>
                  <div>{user.phone}</div>
                </div>
              )}
              {user.village && (
                <div>
                  <strong className="block text-gray-800">Village</strong>
                  <div>{user.village}</div>
                </div>
              )}
              {user.role && (
                <div>
                  <strong className="block text-gray-800">Role</strong>
                  <div>{user.role}</div>
                </div>
              )}
              {user.age_grade && (
                <div>
                  <strong className="block text-gray-800">Age Grade</strong>
                  <div>{user.age_grade.name}</div>
                </div>
              )}
              {user.dob && (
                <div>
                  <strong className="block text-gray-800">Date of Birth</strong>
                  <div>{formatDate(user.dob)}</div>
                </div>
              )}
              <div>
                <strong className="block text-gray-800">Age</strong>
                <div>{user.age ?? computeAge(user.dob)}</div>
              </div>
              {user.created_at && (
                <div>
                  <strong className="block text-gray-800">Joined</strong>
                  <div>{formatDate(user.created_at)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p>User not found.</p>
      )}
    </Card>
  );
};

function formatDate(value: string) {
  try {
    const d = new Date(value);
    return d.toLocaleDateString();
  } catch (e) {
    return value;
  }
}

function computeAge(dob?: string) {
  if (!dob) return "-";
  try {
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age;
  } catch (e) {
    return "-";
  }
}

export default UserProfileShow;
